/** Read-only appointment detail card with quick actions. */

import { CalendarSync, EllipsisVertical, ExternalLink, LogIn } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import {
  APPOINTMENT_STATUS_TRANSITIONS,
  type AppointmentWithRelations,
  canEditAppointment,
  type DateFormat,
  formatClinicAppointmentPenaltyFee,
  formatDuration,
  formatInstantDate,
  formatName,
  isAppointmentTypeId,
} from '@snootsvet/shared'

import { FormattedGridTimeRange } from '@/frontend/components/formatted-datetime'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Button } from '@/frontend/components/ui/button'
import { ConfirmButton } from '@/frontend/components/ui/confirm-button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import { ExpandableContent } from '@/frontend/components/ui/expandable-content'
import {
  CancelAppointmentAction,
  EditAppointmentAction,
  NoShowAppointmentAction,
} from '@/frontend/features/appointments/appointment-actions'
import { AppointmentActivityLog } from '@/frontend/features/appointments/appointment-activity-log'
import { AppointmentStatusBadge } from '@/frontend/features/appointments/appointment-status-badge'
import { getDisplayStatus } from '@/frontend/features/appointments/appointments.utils'
import { StripeNotLinkedBadge } from '@/frontend/features/billing/stripe-not-linked-badge'
import { SpeciesIcon } from '@/frontend/features/patient/species-icon'
import { usePatientAttributes } from '@/frontend/features/patient/use-patient-attributes'
import { usePatchAppointment } from '@/frontend/hooks/api/use-patch-appointment'

interface AppointmentDetailProps {
  appointment: AppointmentWithRelations
  /**
   * The calendar's own frame. This dialog opens from a grid slot, so it reads in
   * the grid's zone and clock rather than the viewer's — otherwise its numbers
   * disagree with the slot that was just clicked. The viewer's reading is annotated
   * instead. Its zone also feeds the edit form's wall-clock inputs.
   */
  fmt: DateFormat
  onEdit: (appointment: AppointmentWithRelations) => void
  onClose: () => void
  onRescheduleOnCalendar?: (appointment: AppointmentWithRelations) => void
}

export function AppointmentDetail({
  appointment,
  fmt,
  onEdit,
  onClose,
  onRescheduleOnCalendar,
}: AppointmentDetailProps) {
  const attributes = usePatientAttributes(appointment.patient)
  const { mutate: patchStatus, isPending } = usePatchAppointment()
  const [unknownTypeDialogOpen, setUnknownTypeDialogOpen] = useState(false)
  const displayStatus = getDisplayStatus(appointment)
  const startDate = new Date(appointment.startTime)
  const endDate = new Date(appointment.endTime)
  const assigneeName = formatName(appointment.staff) || null
  const feeLabel = appointment.clinicId
    ? formatClinicAppointmentPenaltyFee(appointment.clinicId)
    : null

  const durationMins = Math.round((endDate.getTime() - startDate.getTime()) / 60_000)

  return (
    <>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 font-semibold text-xl pr-6">
          <SpeciesIcon species={appointment.patient.species} className="size-5" />
          <Link
            to={`/patients/${appointment.patient.id}`}
            className="text-primary underline-offset-4 hover:underline"
          >
            {appointment.patient.name}
          </Link>
          {!appointment.patient.stripeSubscriptionId && <StripeNotLinkedBadge />}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-0.5 text-sm text-muted-foreground">
          {(['breed', 'age', 'sex', 'neuter'] as const).map((key) => {
            const { icon, label, value } = attributes[key]
            return (
              <span
                key={key}
                className="flex min-w-0 items-center gap-1 [&>svg]:size-3.5 [&>svg]:shrink-0"
              >
                {icon}
                <span className="sr-only">{label}: </span>
                <span className="min-w-0 break-words">{value}</span>
              </span>
            )
          })}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">Date</span>
          <span>{formatInstantDate(startDate, fmt, 'long')}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">Time</span>
          <span>
            <FormattedGridTimeRange start={startDate} end={endDate} fmt={fmt} />
            <span className="ml-2 text-muted-foreground">({formatDuration(durationMins)})</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">Member</span>
          <Link
            to={`/members/${appointment.patient.owner.id}`}
            className="text-primary underline-offset-4 hover:underline"
          >
            {appointment.patient.owner.firstName} {appointment.patient.owner.lastName}
          </Link>
          {!appointment.patient.owner.stripeCustomerId && <StripeNotLinkedBadge />}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">Vet</span>
          <span>{assigneeName ?? '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">Clinic</span>
          <span>{appointment.clinic?.name ?? '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">Type</span>
          <span>{appointment.appointmentTypeName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">Status</span>
          <AppointmentStatusBadge size="sm" status={displayStatus} />
          <div className="flex items-center gap-2 ml-auto">
            {APPOINTMENT_STATUS_TRANSITIONS[appointment.status].has('checked-in') && (
              <ConfirmButton
                variant="ghost"
                disabled={isPending}
                // Only an undo out of no-show needs confirming; checking in from
                // pending/confirmed stays one click.
                bypass={appointment.status !== 'no-show'}
                heading="Undo the no-show?"
                description={
                  feeLabel
                    ? `A ${feeLabel} no-show fee may already have been charged. Reversing this does not refund it — issue the refund in Stripe.`
                    : 'Reversing a no-show does not refund any fee already charged.'
                }
                confirmLabel="Check in"
                onConfirm={() => {
                  patchStatus({ id: appointment.id, status: 'checked-in' })
                  onClose()
                }}
              >
                <LogIn className="size-3" />
                Check in
              </ConfirmButton>
            )}
            {APPOINTMENT_STATUS_TRANSITIONS[appointment.status].has('no-show') && (
              <NoShowAppointmentAction appointment={appointment} onDone={onClose} />
            )}
          </div>
        </div>
        {appointment.visitReason && (
          <div className="flex gap-2">
            <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">
              Visit reason
            </span>
            <span className="break-words min-w-0">{appointment.visitReason}</span>
          </div>
        )}
        {appointment.userNotes && (
          <div className="flex gap-2">
            <span className="text-sm font-semibold text-muted-foreground w-20 shrink-0">
              Member notes
            </span>
            <span className="break-words min-w-0">{appointment.userNotes}</span>
          </div>
        )}
      </div>

      {appointment.activityLog && appointment.activityLog.length > 0 && (
        <div className="p-3 rounded-md bg-muted/33 border border-muted">
          <ExpandableContent>
            <AppointmentActivityLog
              activityLog={appointment.activityLog}
              clinicId={appointment.clinicId}
            />
          </ExpandableContent>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          size="lg"
          variant="outline"
          render={
            <Link
              to={`/patients/${appointment.patient.id}#${appointment.consultation?.id ?? appointment.id}`}
              onClick={onClose}
            />
          }
        >
          View on Timeline
          <ExternalLink className="size-3" />
        </Button>
        <EditAppointmentAction appointment={appointment} timeZone={fmt.timeZone} onEdit={onEdit} />
        {onRescheduleOnCalendar && canEditAppointment(appointment) && (
          <Button
            size="lg"
            onClick={() => {
              if (!isAppointmentTypeId(appointment.appointmentTypeId)) {
                setUnknownTypeDialogOpen(true)
                return
              }
              onRescheduleOnCalendar(appointment)
            }}
          >
            <CalendarSync />
            Reschedule
          </Button>
        )}
        <CancelAppointmentAction appointment={appointment} />
        {canEditAppointment(appointment) && appointment.bookingToken && (
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button variant="outline" size="icon" aria-label="More appointment actions" />
              }
            >
              <EllipsisVertical />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                render={
                  <a
                    href={`${import.meta.env.VITE_CUSTOMER_APP_URL}/booking?token=${appointment.bookingToken}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                }
              >
                <CalendarSync />
                Reschedule via Member Portal
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <AlertDialog open={unknownTypeDialogOpen} onOpenChange={setUnknownTypeDialogOpen}>
        <AlertDialogContent color="destructive">
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot reschedule appointment</AlertDialogTitle>
            <AlertDialogDescription>
              Cannot reschedule unknown appointment type {appointment.appointmentTypeName} on
              calendar
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
